import { useEffect, useState } from "react";
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonModal,
  IonTitle,
  IonToolbar,
  isPlatform,
  useIonToast,
} from "@ionic/react";
import { NavDropdown } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AsYouType,
  E164Number,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { useMutation } from "@tanstack/react-query";
import PhoneInput from "react-phone-number-input";

import Alert from "../common/alert";
import TemplateInput from "../common/template-input";
import PlaceholderReplacer from "../outbound/templates/placeholder";

import ConversationApi from "../../api/conversation";
import { GlobalError, Settings } from "../../interfaces";
import getLanguage from "./utils";

import modalStyles from "../../theme/modal.module.css";
import inputStyles from "../../theme/input.module.css";
import styles from "./styles.module.css";

import "react-phone-number-input/style.css";
import CompaniesDropdown from "../common/account-drop-down";

const isMobile = isPlatform("android") || isPlatform("ios");

const getPreviewText = (message: string, phoneNumber: string | E164Number) => {
  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber);
  const code = getLanguage(parsedPhoneNumber?.country);

  const completeMessage =
    code === "es_MX"
      ? `Hola, espero te encuentres bien!\n\n${message}\n\nSaludos`
      : `Hello, I hope you are well!\n\n${message}\n\nRegards`;

  return completeMessage;
};

export const Wrapper = ({ children }: { children: React.ReactNode }) =>
  isMobile ? (
    <IonContent className="ion-padding">{children}</IonContent>
  ) : (
    <>{children}</>
  );

type NewConversationProps = {
  id?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  settings: Settings;
};

function NewConversation({
  id = "new-conversation-modal",
  isOpen,
  onOpenChange,
  settings,
}: NewConversationProps) {
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState<string | E164Number>("");
  const [name, setName] = useState("");
  const [currentCompany, setCompany] = useState<{ name: string; id: string }>({
    name: settings.company.name,
    id: settings.company.id,
  });
  const [error, setError] = useState(false);
  const [file, setFile] = useState<File | null>();

  const intl = useIntl();
  const [present] = useIonToast();

  const onBeforeDismiss = (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e?.preventDefault?.();

    onOpenChange(false);
    setMessage("");
    setPhone("");
    setName("");
    setFile(null);
    return setError(false);
  };

  const { mutate: mutateNewConversation, isPending: isCreatingConversation } =
    useMutation({
      mutationFn: ConversationApi.newConversationV2,
      onSuccess: async (data) => {
        if (data.error) {
          return present({
            message: data.error?.[intl.locale],
            duration: 5500,
            color: "light",
            position: "top",
            cssClass: "error-toast",
          });
        }
        onBeforeDismiss();
        present({
          message: intl.formatMessage({ id: "newConv.success" }),
          duration: 6000,
          color: "light",
          position: "top",
          cssClass: "success-toast",
        });
        return;
      },
      onError: (e: GlobalError) => {
        if (e?.error) {
          return present({
            message: e.error?.[intl.locale],
            duration: 5500,
            color: "light",
            position: "top",
            cssClass: "error-toast",
          });
        }
        return present({
          message: intl.formatMessage({ id: "common.tryItLater" }),
          duration: 5000,
          color: "light",
          position: "top",
          cssClass: "error-toast",
        });
      },
    });

  const onBeforeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValidPhone = isValidPhoneNumber(phone);
    if (!isValidPhone) {
      return setError(() => true);
    }

    const phoneNumber = new AsYouType().input(phone);

    const formData = new FormData();
    if (file) {
      formData.append("files", file);
    }
    formData.append("phoneNumber", phoneNumber);
    formData.append("message", message);
    formData.append("name", name);
    formData.append("companyId", String(currentCompany?.id));

    return mutateNewConversation(formData);
  };

  const onBeforeSelectCompany = (id: string) => {
    const company = settings.companies.find((comp) => comp.id === id);
    if (company) {
      setCompany(() => ({ name: company.name, id: company.id }));
    }
  };

  useEffect(() => {
    if (settings.company.id && !currentCompany) {
      setCompany(() => ({
        name: settings.company.name,
        id: settings.company.id,
      }));
    }
  }, [settings.multiAccount, settings.company]);

  return (
    <IonModal id={id} isOpen={isOpen} onDidDismiss={() => onBeforeDismiss()}>
      {isMobile && (
        <IonHeader>
          <IonToolbar style={{ paddingLeft: 0, paddingRight: 0 }}>
            <IonTitle>
              <FormattedMessage id="newConv.Title" />
            </IonTitle>
          </IonToolbar>
        </IonHeader>
      )}
      <Wrapper>
        <form
          id="new-conversation"
          className={isMobile ? "" : "inner-content"}
          onSubmit={onBeforeSubmit}
        >
          {!isMobile && (
            <div className={modalStyles.header}>
              <h2 className="mb-0 fs-4">
                <FormattedMessage id="newConv.Title" />
              </h2>
              <p className="mb-0 fs-3 description">
                <FormattedMessage id="newConv.Desc" />
              </p>
            </div>
          )}

          <div
            className={modalStyles.body}
            style={{ padding: isMobile ? "1rem 1.25em" : "1rem" }}
          >
            {isMobile && (
              <p className="mb-0 fs-3 description">
                <FormattedMessage id="newConv.Desc" />
              </p>
            )}
            {error && (
              <Alert
                type="danger"
                className="mb-1"
                message={intl.formatMessage({ id: "newConv.invalidPhone" })}
              />
            )}
            {settings.multiAccount && (
              <div className="flex flex-column gap-2 w-100">
                <label htmlFor="companyId" className="fs-3">
                  {`${intl.formatMessage({ id: "common.account" })}`}
                </label>
                <NavDropdown
                  title={currentCompany?.name}
                  className={styles.accountSwitcher}
                >
                  <CompaniesDropdown
                    currentCompany={currentCompany}
                    companies={settings.companies}
                    onSelectCompany={onBeforeSelectCompany}
                    variant="light"
                  />
                </NavDropdown>
              </div>
            )}
            <div className="flex flex-column gap-2">
              <label htmlFor="phone" className="fs-3">
                {`${intl.formatMessage({ id: "common.phone" })}*`}
              </label>
              <PhoneInput
                international
                defaultCountry="MX"
                value={phone}
                onChange={(value) => setPhone(value as E164Number)}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="name" className="fs-3">
                {`${intl.formatMessage({
                  id: "common.contactName",
                })} (${intl.formatMessage({ id: "common.optional" })})`}
              </label>
              <input
                id="name"
                name="name"
                type="text"
                className={inputStyles.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
                disabled={isCreatingConversation}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="message" className="fs-3">
                {`${intl.formatMessage({ id: "common.message" })}*`}
              </label>
              <TemplateInput
                id="message"
                rows={isMobile ? 6 : 2}
                disabled={isCreatingConversation}
                value={message}
                setText={setMessage}
                maxLength={900}
                variables={[]}
                disableBreakLine
                allowFiles
                file={file}
                setFile={setFile}
              />
            </div>
            {message && message.length > 0 && (
              <div className="flex flex-column gap-2">
                <label className="fs-3">
                  <FormattedMessage id="outbound.templatePreview" />
                </label>
                <div className="chat-item mx-2">
                  <PlaceholderReplacer
                    template={getPreviewText(message, phone)}
                    placeholders={[]}
                  />
                </div>
              </div>
            )}
          </div>

          {!isMobile && (
            <div className={modalStyles.footer}>
              <button
                className="btn btn-visito-light w-100"
                onClick={onBeforeDismiss}
                disabled={isCreatingConversation}
              >
                <FormattedMessage id="common.cancel" />
              </button>
              <button
                className="btn btn-visito-primary w-100"
                type="submit"
                style={{
                  pointerEvents: isCreatingConversation ? "none" : "auto",
                }}
              >
                {isCreatingConversation ? (
                  "..."
                ) : (
                  <FormattedMessage id="common.create" />
                )}
              </button>
            </div>
          )}
        </form>
      </Wrapper>
      {isMobile && (
        <IonFooter>
          <IonToolbar style={{ paddingLeft: 0, paddingRight: 0 }}>
            <div
              className={modalStyles.footer}
              style={{ borderTop: "0px", padding: "0.5rem 1em" }}
            >
              <button
                className="btn btn-visito-light w-100"
                onClick={onBeforeDismiss}
                disabled={isCreatingConversation}
              >
                <FormattedMessage id="common.cancel" />
              </button>
              <button
                className="btn btn-visito-primary w-100"
                type="submit"
                form="new-conversation"
                style={{
                  pointerEvents: isCreatingConversation ? "none" : "auto",
                }}
              >
                {isCreatingConversation ? (
                  "..."
                ) : (
                  <FormattedMessage id="common.create" />
                )}
              </button>
            </div>
          </IonToolbar>
        </IonFooter>
      )}
    </IonModal>
  );
}

export default NewConversation;
